// src/utils/docxUtils.js
import { 
    Document, 
    Packer, 
    Paragraph, 
    Table, 
    TableRow, 
    TableCell, 
    TextRun, 
    WidthType, 
    AlignmentType, 
    HeadingLevel, 
    BorderStyle, 
    UnderlineType 
  } from "docx";
  import Swal from "sweetalert2";
  import { saveAs } from "file-saver";
  import MarkdownIt from "markdown-it";
  
  /**
   * Função auxiliar para criar parágrafos com estilo e margem superior
   */
  const createParagraph = (text, options = {}) => {
    return new Paragraph({
      children: [
        new TextRun({
          text: text || "",
          bold: options.bold || false,
          italics: options.italics || false,
          underline: options.underline ? { type: UnderlineType.SINGLE } : undefined,
          font: "Arial",
          size: options.size || 24, // 12pt
        }),
      ],
      heading: options.heading || undefined,
      spacing: { 
        before: options.spacingBefore || 200, // Espaçamento antes do parágrafo
        after: options.spacingAfter || 200 
      },
      alignment: options.alignment || AlignmentType.LEFT,
    });
  };
  
  /**
   * Função auxiliar para criar tabelas com bordas e espaçamento
   */
  const createTable = (tableNode) => {
    const tableRows = [];
  
    tableNode.querySelectorAll("tr").forEach((tr, rowIndex) => {
      const tableCells = [];
      tr.querySelectorAll("th, td").forEach((td, cellIndex) => {
        tableCells.push(
          new TableCell({
            width: {
              size: 5000, // Ajuste conforme necessário
              type: WidthType.DXA,
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            },
            shading: rowIndex === 0 ? { fill: "D3D3D3" } : undefined, // Cor de fundo para cabeçalhos
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: td.textContent || "",
                    bold: rowIndex === 0, // Negrito para cabeçalhos
                    font: "Arial",
                    size: 24, // 12pt
                  }),
                ],
                alignment: AlignmentType.LEFT,
                spacing: { after: 0 },
              }),
            ],
          })
        );
      });
  
      tableRows.push(
        new TableRow({
          children: tableCells,
        })
      );
    });
  
    return new Table({
      rows: tableRows,
      width: {
        size: 9000, // Ajuste para garantir que a tabela não ultrapasse as margens
        type: WidthType.DXA,
      },
      alignment: AlignmentType.CENTER,
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
      },
      spacing: { before: 200, after: 200 }, // Espaçamento antes e depois da tabela
    });
  };
  
  /**
   * Converte HTML para elementos do docx
   * Suporta parágrafos, cabeçalhos e tabelas
   */
  export const convertHtmlToDocxElements = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const elements = [];
  
    // Itera sobre os nós filhos do body do HTML
    doc.body.childNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
  
      switch (node.nodeName) {
        case "H1":
          elements.push(
            createParagraph(node.textContent, { 
              heading: HeadingLevel.HEADING_1, 
              bold: true, 
              size: 32, // 16pt
              spacingBefore: 800, // 2,78 polegadas (ajuste conforme necessário)
            })
          );
          break;
        case "H2":
          elements.push(
            createParagraph(node.textContent, { 
              heading: HeadingLevel.HEADING_2, 
              bold: true, 
              size: 28, // 14pt
              spacingBefore: 600, // 2,08 polegadas
            })
          );
          break;
        case "H3":
          elements.push(
            createParagraph(node.textContent, { 
              heading: HeadingLevel.HEADING_3, 
              bold: true, 
              size: 26, // 13pt
              spacingBefore: 500, // 1,74 polegadas
            })
          );
          break;
        case "P":
          elements.push(createParagraph(node.textContent));
          break;
        case "TABLE":
          elements.push(createTable(node));
          break;
        // Adicione mais casos conforme necessário (listas, imagens, etc.)
        default:
          elements.push(createParagraph(node.textContent));
          break;
      }
    });
  
    return elements;
  };
  
  /**
   * Gera e baixa um documento DOCX a partir do conteúdo Markdown.
   * @param {string} markdownContent - Conteúdo em Markdown a ser convertido.
   */
  export const generateAndDownloadDocx = (markdownContent) => {
    // Inicializa o markdown-it com suporte a tabelas
    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      breaks: true,
      // Habilita o suporte a tabelas do GFM
    });
  
    // Converte Markdown para HTML
    const htmlContent = md.render(markdownContent);
    console.log("HTML Content:", htmlContent);
  
    // Converte HTML para elementos do docx
    const docxElements = convertHtmlToDocxElements(htmlContent);
    console.log("Docx Elements:", docxElements);
  
    // Cria o documento com estilos globais e margens
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,    // 1 polegada
                bottom: 1440, // 1 polegada
                left: 1440,   // 1 polegada
                right: 1440,  // 1 polegada
              },
            },
          },
          children: docxElements,
        },
      ],
    });
  
    // Empacota o documento e gera o Blob
    Packer.toBlob(doc)
      .then((blob) => {
        console.log("DOCX Blob:", blob);
        saveAs(blob, "documento.docx");
        // Remover o popup de sucesso
        // Swal.fire("Documento DOCX gerado com sucesso!");
      })
      .catch((error) => {
        console.error("Erro ao gerar o DOCX:", error);
        Swal.fire("Erro ao gerar o documento. Por favor, tente novamente.");
      });
  };
  